/**
 * Log entry types (RFC 0215: Log substrate).
 *
 * CANONICAL ON-DOC SHAPE = Searugast's automorph representation, so the two
 * peers read/write one byte-compatible Automerge document. Concretely an entry
 * is:
 *
 *   entries[id] = {
 *     id, parents, createdAt,          // immutable core; createdAt is i64 millis
 *     payload: { type, payload: {...} } // adjacently-tagged envelope
 *   }
 *
 * The discriminant is `payload.type` (snake_case) and the variant fields live
 * under `payload.payload` (camelCase). The double nesting is intentional: it is
 * automorph 0.2's adjacent-tag representation (`{type, payload}`), which keeps
 * the CRDT `text` field at a stable path regardless of variant.
 *
 * The core variants match Searugast's EntryPayload (message / run /
 * inference_step / worker_migration); `extension` is a Mod addition carrying
 * the open vocabulary (object_*, session_change). See RFC 0215 §3.1 and §3.7.
 *
 * RFC 0262 — there is no standalone `model_selection` entry: Searugast dropped
 * it, and model selection is EMBEDDED (`run.requestedModel`, and whiffletree's
 * REQUIRED `inference_step.resolvedModel`). Emitting one would be an unknown
 * variant to a strict reader.
 *
 * (Decisions, if ever needed, are a derived projection over run DAGs, not a
 * stored entry type.)
 */

export type EntryId = string
export type ToolWorkerId = string
export type ToolUseId = string
/** An Automerge DocumentId at runtime; string in the pure model. */
export type DocId = string
/** Milliseconds since the Unix epoch (Searugast TimestampMs, an i64). */
export type TimestampMs = number

// ============================================================================
// message  (Searugast MessagePayload)
// ============================================================================

export type MessageRole =
  | 'user'
  | 'assistant'
  | 'tool_result'
  | 'system'
  | 'custom'

export type MessageStatus = 'streaming' | 'completed' | 'failed' | 'cancelled'

export interface ToolUseBlock {
  id: ToolUseId
  name: string
  arguments: unknown
}

/**
 * RFC 0288: a pointer to an attachment document. Scalar-only by design, so the
 * bytes are reachable from a message but never resident in the session log.
 */
export interface AttachmentRef {
  docId: DocId
  name: string
  mimeType: string
  bytes: number
  width?: number
  height?: number
}

export interface MessageData {
  /** RFC 0345 S6: authenticated human posts, bound to their actual session
   * and entry identity. Unsigned legacy messages are never automatic replies. */
  by?: string
  postedBy?: string
  postSignature?: string
  role: MessageRole
  status: MessageStatus
  /** Inline streaming text (Automerge Text at runtime). */
  text: string
  /** Tool-use requests; empty on non-assistant roles. */
  toolUses: ToolUseBlock[]
  runId?: EntryId
  stepId?: EntryId
  /** For role 'tool_result': the tool_use block this result answers. */
  toolUseId?: ToolUseId
  isError?: boolean
  stopReason?: string | null
  usage?: unknown
  metadata?: Record<string, unknown>
  /** Heavy content spilled to a referenced document instead of inline `text`. */
  contentRef?: DocId | null
  /** RFC 0288: attachment pointers. Complete at append; never re-assigned. */
  attachments?: AttachmentRef[]
  /**
   * RFC 0304 3d: screenshots captured with the view at send time.
   *
   * Separate from `attachments`: these are context the shell supplied, not
   * files the person attached, so transcript surfaces must not draw them.
   */
  viewAttachments?: AttachmentRef[]
  /**
   * RFC 0297 §4e: the passage the person was pointing at when they wrote this.
   *
   * FLAT SCALARS, for the reason `RunDispatch` gives: the codec stringifies an
   * unknown nested object on decode, so `{ documentId, label }` would come back
   * as one useless string. Three fields, written together at append.
   *
   * A RECORD, not an instruction. A selection lives in window state and changes
   * on every click (§4e); this is the one moment it is worth keeping, because a
   * message saying "make this dashed" is unreadable later without it.
   */
  selectionDocumentId?: DocId
  selectionLabel?: string
  /** The app's opaque handle for what it named. Never resolved by the host. */
  selectionRef?: string
  /**
   * RFC 0307 3c: said on a voice call, not typed. Every line of one call carries
   * the same id, which is how the thread groups them. A spoken line is written
   * with no run; the person's line only becomes a prompt when the voice relays
   * it and a run is queued on it. Flat scalars, for the codec.
   */
  voiceCallId?: string
  /** Who said the spoken line: the person, or the voice model. */
  spokenBy?: 'person' | 'voice'
  /** On an addressed relay, the session where the whole call is recorded. */
  voiceHomeSessionId?: string
  /** The person's original spoken line in that home session. */
  voiceHomeEntryId?: EntryId
  /**
   * RFC 0304 3d: what was on screen when the person sent this, as a JSON
   * `MessageView`.
   *
   * ONE STRING, not flat fields, because it is a list of panes and the codec
   * stringifies nested objects anyway. Encoded once at append and parsed once
   * by `projectView`, so nothing else reads the string.
   */
  viewJson?: string
}

/** What the composer stamps onto a message it sends (RFC 0297 §4e). */
export interface MessageSelection {
  documentId: string
  label: string
  ref?: string
}

/** One pane on screen when a message was sent (RFC 0304 3d). */
export interface ViewPane {
  kind: 'document' | 'session' | 'session-list' | 'terminal' | 'empty'
  id?: string
  title?: string
  focused: boolean
}

/**
 * The view a message was sent from (RFC 0304 3d): which panes were open and
 * which had focus. Captured at send, so "this" means what was on screen then.
 */
export interface MessageView {
  panes: ViewPane[]
  space?: string
}

// ============================================================================
// run
// ============================================================================

export type RunStatus =
  | 'queued'
  | 'running'
  | 'waiting_for_tools'
  | 'completed'
  | 'failed'
  | 'cancelled'

export interface ModelRef {
  provider: string
  id: string
  thinkingLevel?: string
}

/**
 * Whiffletree `ModelSelection`: the on-doc `{ provider, model }` a run requested
 * or a step resolved. `source`/`thinkingLevel` are mod extras (optional); the
 * upstream projection ignores unknown keys, so carrying them stays compatible.
 */
export interface ModelSelection {
  provider: string
  model: string
  source?: string
  thinkingLevel?: string
}

/**
 * RFC 0286 §5a — who asked to hear about this turn, at the moment it is
 * dispatched.
 *
 * The CALLER's shape. On the doc it is flattened onto the run
 * (`dispatchFrom` / `dispatchId` / `dispatchRepliedAt`), because the codec
 * stringifies a nested object on decode and refuses to re-encode one after a
 * mutation — so a nested dispatch could be neither read back nor marked
 * delivered. Flat scalars are the rule everywhere else on this log for the
 * same reason.
 */
/** What a subscriber can hear from a session (RFC 0348 3h): a turn that
 *  ended, or one that asked a question and is waiting. */
export type StopEvent = 'ended' | 'asked'

/** Every event, which is what a subscriber hears unless it says otherwise. */
export const STOP_EVENTS: readonly StopEvent[] = ['ended', 'asked']

/** The events a flat `'ended,asked'` field names; empty or absent is all. */
export function stopEventsOf(value: unknown): readonly StopEvent[] {
  const named = String(value ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter((v): v is StopEvent => v === 'ended' || v === 'asked')
  return named.length > 0 ? named : STOP_EVENTS
}

export interface RunDispatch {
  /** The session that dispatched this turn, and the one the reply is written to. */
  fromSessionId: string
  /** RFC 0348 3h: which events the dispatcher wants. Absent is both. */
  events?: readonly StopEvent[]
  /** Correlates the reply with this dispatch, so a sender with several
   *  outstanding can tell them apart without matching on text. */
  dispatchId: string
}

export interface RunData {
  promptMessageId: EntryId
  /** RFC 0345 S6: the person whose message is being answered. */
  by?: string
  /** Harness on legacy runs; the answering agent's key on protected runs.
   * requestedModel.provider continues to choose the harness/model. */
  agent: string
  /** RFC 0345 S6: an agent owner's device authorized this exact prompt and
   * address. Flat signed fields, not editable metadata as authority. */
  authorizedBy?: string
  authorization?: string
  /** RFC 0345 harness H8: the interrupted run this one retries, inside the
   * signed bytes. Only the person's own tap writes one. */
  retryOf?: EntryId
  /** RFC 0345 harness H8: set by the key's host on a run of its own that a
   * restart cut off mid-turn; the run is `failed` and is never reclaimed. */
  interruptedAt?: number
  status: RunStatus
  /**
   * Whiffletree RunEvent.requestedModel (was mod's open `model`). Optional and
   * absent in mod's queue path today: the model resolves at inference time, not
   * when the run is queued.
   */
  requestedModel?: ModelSelection | null
  /**
   * World skills this turn was asked for (RFC 0283 S5), by catalog id, in the
   * order chosen.
   *
   * A MOD ADDITION, not whiffletree: optional, so a run written without it
   * decodes everywhere, and searugast and iOS ignore it exactly as they ignore
   * the World's `skills` field. Carried on the RUN rather than the message
   * because it instructs the host how to run the turn rather than being
   * something the person said, and because it then stays readable afterwards:
   * a reader can see why the agent behaved the way it did.
   */
  skills?: string[] | null
  /**
   * RFC 0286 §5a — this turn was DISPATCHED by another session that asked to
   * hear back when it ends.
   *
   * A MOD ADDITION, like `skills`: optional, so a run written without it decodes
   * everywhere and a strict reader ignores it exactly as it ignores `skills`.
   *
   * Carried on the RUN rather than in a registry of outstanding dispatches
   * because the run IS the turn being waited on: nothing has to be kept in sync
   * with it, a daemon that dies mid-turn leaves no orphan record, and the reply
   * is found by reading the log that owes it. If a hop count is ever needed to
   * break a notify cycle (RFC 0286 §10), this is where it rides.
   *
   * FLAT, like every other scalar here: the codec stringifies a nested object on
   * decode and skips it on re-encode after a mutation, so a nested dispatch
   * could be neither read back nor marked delivered.
   */
  /** The session to tell when this turn ends. Absent = nobody asked. */
  dispatchFrom?: string
  /** Correlates the reply with the dispatch that asked for it. */
  dispatchId?: string
  /** RFC 0348 3h: the events the dispatcher wants, flat as `'ended,asked'`.
   *  Absent is both. */
  dispatchEvents?: string
  /**
   * When the reply was delivered; absent or 0 means the turn still owes one.
   *
   * The receipt lives HERE, on the run being waited on, so a re-scan of this log
   * — after a restart, from a second device, on a change event that fires twice
   * — can never notify twice, and nothing has to remember what it already sent.
   */
  dispatchRepliedAt?: TimestampMs
  /**
   * RFC 0336 S4 — the question this turn stopped on that was already passed
   * back to `dispatchFrom`, by interaction id. A turn that asks something is
   * not over, so `dispatchRepliedAt` cannot be its receipt; this is, one
   * question at a time, so a re-scan never relays the same question twice.
   */
  dispatchAskedFor?: string
  /**
   * RFC 0348 3h: the last stop of this turn delivered to the World agent, as
   * `run:status:question`. A turn that asks and then finishes is two stops,
   * each delivered once; a re-scan after a restart delivers nothing again.
   * Empty means a delivery that failed and is owed again.
   */
  worldHeard?: string
  /** RFC 0348 3h: last stop delivered to each subscriber, JSON encoded as a
   * flat string because the run codec does not preserve nested fields. */
  stopHeard?: string
  /**
   * RFC 0348 3d: the session whose `send_message` queued this turn. Absent
   * when a person, or an event, did. How the World agent's two-in-a-row bound
   * counts.
   */
  sentBy?: string
  /**
   * RFC 0302 S3 — this turn is a BRANCH: its context is pinned to its own
   * ancestry and never widens to whatever else has landed by the time it runs.
   *
   * The routing decision 3c describes, recorded rather than re-derived. A post
   * arriving while the agent works is a CONTINUE by default: it waits for the
   * active turn and then reads the frontier as it stands, which after a
   * serialized drain is that turn's completed head. Only a branch — work the
   * target judged coherent enough to advance on its own — keeps the heads the
   * post was sent against and stays blind to its sibling's answer.
   *
   * On the run rather than the message, because it governs how the TURN is fed,
   * and because a message may be re-run. Absent means continue, so every run
   * ever written behaves exactly as it did before this existed.
   *
   * A flat boolean, per the rule the dispatch fields above give.
   */
  branch?: boolean
  currentStepId?: EntryId | null
  startedAt?: TimestampMs | null
  /**
   * The device that claimed this run (RFC 0301 S12). A MOD ADDITION, optional
   * so a run written without it decodes everywhere.
   *
   * The claim is a one-way flip on the log and nothing ever un-flips it, so a
   * worker that dies mid-turn leaves the run `running` forever. Knowing WHO
   * claimed it turns "is this run stranded" into a question World presence
   * already answers: claimed by a device that is no longer present. Absent
   * when the claiming worker had no identity, and such a run cannot be judged.
   */
  claimedBy?: ToolWorkerId | null
  completedAt?: TimestampMs | null
  error?: string | null
  // NB: no per-run device field. Whiffletree's RunEvent has none, and mod's
  // `initialToolWorkerId` was retired (RFC 0261 slice 3) — placement is the
  // thread's `selected_tool_worker` (latest worker_migration), so a run is served
  // by whichever device the thread selected, not one addressed on the run.
}

// ============================================================================
// inference_step
// ============================================================================

export type StepStatus =
  | 'streaming'
  | 'waiting_for_tools'
  | 'completed'
  | 'failed'
  | 'cancelled'

export interface InferenceStepData {
  runId: EntryId
  stepIndex: number
  status: StepStatus
  /** Transcript heads used to build the provider request. */
  inputHeads: EntryId[]
  /** Whiffletree InferenceStepEvent.resolvedModel: the model this step ran. */
  resolvedModel?: ModelSelection | null
  outputMessageId?: EntryId | null
  providerResponseId?: string | null
  error?: string | null
}

// ============================================================================
// worker_migration
// ============================================================================

export interface WorkerMigrationData {
  /**
   * Whiffletree `WorkerMigrationEvent.workerId`: the worker now selected. REQUIRED
   * (non-Option upstream), so a migration missing it fails the strict reader on
   * attach — this is the field placement writes, and the latest migration's
   * `workerId` IS `selected_tool_worker`.
   */
  workerId: ToolWorkerId
  /** mod extra (tolerated superset — whiffletree ignores unknown fields, see
   *  wf_tolerance): the previously-active worker, for the "context changed from X
   *  to Y" note. Optional; absent on the first selection. */
  fromWorkerId?: ToolWorkerId
  runId?: EntryId
  reason?: string
}

// ============================================================================
// extension  (the open vocabulary — RFC 0215 §3.8)
// ============================================================================

/**
 * The single core envelope that carries any developer- or first-party-defined
 * event. Peers that don't handle `extensionType` still load, preserve, and
 * round-trip the entry (the `payload` is opaque to them) — forward-compatibility
 * without per-type schema changes. `object_*` is the first extension.
 */
export interface ExtensionData {
  /** Namespaced (owner/package-prefixed), e.g. `mod.object_created`. */
  extensionType: string
  /** Owned by the definer; consumers switch on it. */
  version: number
  /** Opaque to peers without a handler; round-trips losslessly. */
  payload: Record<string, unknown>
}

/** A spoken line at home was addressed to another session (RFC 0307 3c). */
export const VOICE_RELAYED_EXTENSION = 'mod.voice_relayed' as const

/** Object/file lifecycle, shipped as `mod.object_*` extension events. */
export const OBJECT_EXTENSION = {
  created: 'mod.object_created',
  edited: 'mod.object_edited',
  deleted: 'mod.object_deleted',
} as const

/** Payload shape of a `mod.object_*` extension event (ExtensionData.payload). */
export interface ObjectData {
  path: string
  objectType: string
  /** Referenced content document; null on delete (tombstone). */
  docId: DocId | null
}

/**
 * RFC 0233 (collaboration tier): a session PARTICIPANT, reified as a log object.
 *
 * A participant entry admits a member to a target (session/message/document/
 * task) and is what the per-session sync gate reads. It follows the same emit
 * path as `object_*` (a single writer via
 * `SessionRepository.appendParticipantEntry`), so every client records it
 * byte-identically. It stores `memberId` (the auth-tier identity), never a
 * handle, so renaming a handle never breaks an existing grant.
 *
 * RFC 0233 created these entries by @-mentioning somebody, which is why the wire
 * value says `mention`. RFC 0292 gave that word to a reference inside prose,
 * which grants nothing, and renamed the access half to what it does. THE WIRE
 * VALUE STAYS: every entry already written carries this string, and changing it
 * would project as "no participants", which the gate reads as "admit nobody but
 * the creator".
 */
export const PARTICIPANT_EXTENSION = 'mod.object_mention' as const

/** What a participant entry can be scoped to. */
export type ParticipantTargetKind = 'session' | 'message' | 'document' | 'task'

/**
 * Payload shape of a participant event (ExtensionData.payload).
 *
 * Flat on-doc (`targetKind`/`targetId`) to match `ObjectData`: the codec leaves
 * an extension's inner payload raw, so projections read flat scalars via
 * `String()` coercion.
 */
export interface ParticipantData {
  /** Who is admitted (= ProjectMember.userId). */
  memberId: string
  /** Who admitted them (= ProjectMember.userId). */
  by: string
  targetKind: ParticipantTargetKind
  targetId: string
  /** When the entry was written (millis). */
  at: TimestampMs
}

/**
 * RFC 0235 (session version control): a session's code change reified on the log.
 *
 * Dual tracking — git/jj holds the authoritative file bytes; the log carries a
 * `mod.session_change` extension event that LINKS to the VCS change (its ref +
 * the touched paths) and sits in the session's context stream. The log does NOT
 * duplicate file content as CRDT objects; it points at the VCS ref. Emitted via
 * the single writer `SessionRepository.appendSessionChangeEntry`.
 *
 * Stored flat on-doc (`vcsBackend`/`vcsCommit`/`vcsBranch`) to match `ObjectData`
 * and `ParticipantData`: the codec leaves an extension's inner payload raw, so
 * projections read flat scalars via `String()`. `projectSessionChanges` re-nests
 * these as `vcsRef`.
 */
export const SESSION_CHANGE_EXTENSION = 'mod.session_change' as const

/** Payload shape of a `mod.session_change` event (ExtensionData.payload). */
export interface SessionChangeData {
  /** The session this change belongs to (the log linkage key). */
  sessionId: string
  /** Which VCS owns the bytes ('git' | 'jj'). */
  vcsBackend: string
  /** The commit sha (git) / change id (jj) the change landed as. */
  vcsCommit: string
  /** The ref the commit sits on (git: branch name `mod/session/<id>`). */
  vcsBranch: string
  /** Paths touched by the change (no content — line-based bytes live in the VCS). */
  files: string[]
  /** When the change was recorded (millis). */
  at: TimestampMs
}

/**
 * RFC 0235 — terminal VCS lifecycle of a session, reified on the log alongside
 * `session_change`. `session_discarded` = the change was thrown away (git branch
 * dropped / jj abandon); `session_merged` = it was merged into the base (carries
 * the real merge commit). The base never moves on discard; the session and its
 * context remain readable on the log either way.
 */
export const SESSION_DISCARDED_EXTENSION = 'mod.session_discarded' as const
export const SESSION_MERGED_EXTENSION = 'mod.session_merged' as const

/**
 * RFC 0302 S4 — this session is a BRANCH of another one, written on the child's
 * own log as its first entry.
 *
 * On the CHILD, not on the parent, for the reason the dispatch record lives on
 * the run it is owed by: the child is the thing that needs to know where it came
 * from, and a parent that has fanned out six branches should not have to be
 * opened to find out what any one of them is. Drawing the parent reads the
 * project registry (`parentSessionId`), which costs no hydration; opening the
 * child reads this, which it has already hydrated to read anything at all.
 *
 * It is also the origin line the subthread shows — "From 'the post that caused
 * this'" — and the Back target a pane returns to (S7).
 */
export const SESSION_BRANCHED_EXTENSION = 'mod.session_branched' as const

/** Payload of a `mod.session_branched` event (ExtensionData.payload). */
export interface SessionBranchedData {
  /** The session this branched FROM. */
  sourceSessionId: string
  /** The post that caused it, on that session's log. Empty if unknown. */
  sourceMessageId: string
  /**
   * The causal frontier the branch was taken against.
   *
   * An array rather than flat scalars, per `session_change.files`: an extension
   * payload is left raw by the codec and its projection coerces each element,
   * so a list survives where a nested object would not.
   */
  sourceHeads: string[]
  at: TimestampMs
}

/** Payload of a `mod.session_discarded` event (ExtensionData.payload). */
export interface SessionDiscardedData {
  sessionId: string
  /** The dropped branch (empty if the project had no VCS change). */
  vcsBranch: string
  at: TimestampMs
}

/** Payload of a `mod.session_merged` event (ExtensionData.payload). */
export interface SessionMergedData {
  sessionId: string
  /** The branch that was merged. */
  vcsBranch: string
  /** The resulting merge commit on the base. */
  vcsCommit: string
  /** The base it was merged into. */
  vcsInto: string
  at: TimestampMs
}

/**
 * A turn that stops and waits for a human, reified on the log.
 *
 * The problem this solves is stated as a known gap in the harness driver
 * (`@mod/harness` drive/claude-code.ts, `allowWrites`): "a prompt raised by a
 * laptop harness needs answering from the phone that enqueued the run, and that
 * plumbing does not exist yet". Because it did not exist, machine writes are
 * denied outright rather than asked about.
 *
 * The fix is not a socket between the harness and whichever surface happens to
 * be open — that is the coupling the log exists to avoid. A pending question is
 * just another log object: the agent appends `mod.interaction_requested` and
 * blocks; ANY surface syncing that session projects it, renders it, and appends
 * `mod.interaction_answered`; the waiter wakes on the correlated id. Ask on the
 * laptop, answer on the phone, and the transcript keeps the exchange forever
 * because the question and its answer are entries like any other.
 *
 * `kind` is the open axis. `question` (AskUserQuestion) is the first; tool
 * approval is the same shape — pause, ask, resume — and is meant to land here
 * rather than growing a parallel mechanism.
 *
 * The kind-specific body rides as a JSON STRING rather than a nested object.
 * Every other extension payload on this log is deliberately flat, because the
 * codec leaves an extension's inner payload raw and projections coerce scalars
 * with `String()` (see `ParticipantData`, `SessionChangeData`). A question carries
 * nested questions/options/previews, which that convention cannot express, so it
 * is serialized once at the boundary and parsed once in the projection. Opaque
 * to the log, lossless, and it keeps the flat-scalar rule intact.
 */
export const INTERACTION_REQUESTED_EXTENSION =
  'mod.interaction_requested' as const
export const INTERACTION_ANSWERED_EXTENSION =
  'mod.interaction_answered' as const

/** The kinds of pause a turn can take. Open vocabulary; `question` is S1. */
export type InteractionKind = 'question' | 'tool_approval'

/** Payload of a `mod.interaction_requested` event (ExtensionData.payload). */
export interface InteractionRequestedData {
  /** Correlates the request with its answer. Unique per request. */
  interactionId: string
  /** The session whose turn is blocked. */
  sessionId: string
  kind: InteractionKind
  /** Short human label for the surface that renders it (a chip, a row). */
  title: string
  /** Kind-specific body, JSON-encoded (see above). */
  bodyJson: string
  at: TimestampMs
}

/** How a pending interaction ended. */
export type InteractionOutcome = 'answered' | 'cancelled'

/** Payload of a `mod.interaction_answered` event (ExtensionData.payload). */
export interface InteractionAnsweredData {
  /** The `interactionId` of the request being answered. */
  interactionId: string
  sessionId: string
  outcome: InteractionOutcome
  /** Why it was cancelled ('timeout' | 'dismissed'). Absent when answered. */
  reason?: string
  /** Kind-specific answer, JSON-encoded. Empty string when cancelled. */
  answerJson: string
  /** Who answered (= ProjectMember.userId). Empty when the waiter timed out. */
  by: string
  at: TimestampMs
}

/**
 * RFC 0286 §5a — a dispatched turn has ended, told to the session that sent it.
 *
 * An EVENT, never a user message. Appended as one it would be indistinguishable
 * from the person typing, and an orchestrator would act on "the essay session
 * answered" as though it were an instruction. As an event it carries who
 * answered and which dispatch it answers, which is also what lets a thread
 * render it as a reference rather than pasted prose.
 *
 * Flat scalars, per the convention every other extension payload here follows:
 * the codec leaves an extension's inner payload raw and projections coerce with
 * `String()`.
 */
export const SESSION_REPLIED_EXTENSION = 'mod.session_replied' as const

/** Payload of a `mod.session_replied` event (ExtensionData.payload). */
export interface SessionRepliedData {
  /** The session that answered, and the run of ITS turn that just finished. */
  sessionId: string
  runId: string
  /** The dispatch this answers. */
  dispatchId: string
  /**
   * First line of the reply. The rest is read with `read_session`, which is
   * bounded: inlining a whole answer would put a second transcript on the
   * sender's log every time, and that is how a keeper session becomes the
   * biggest document in the project.
   */
  summary: string
  at: TimestampMs
}

/**
 * RFC 0348 3a: an agent somewhere in the World stopped, written on the World
 * agent's log.
 *
 * The same shape and the same reasons as `mod.session_replied`: an event, so it
 * is never read as the person typing, and a pointer with one line, so the rest
 * is a `read_session` away. It differs in who asked: nobody did. Every stop is
 * reported, and the World agent decides whether it needs anything.
 */
export const SESSION_STOPPED_EXTENSION = 'mod.session_stopped' as const

/** Payload of a `mod.session_stopped` event (ExtensionData.payload). */
export interface SessionStoppedData {
  /** The session that stopped, and the run of its turn. */
  sessionId: string
  runId: string
  /** RFC 0348 3h: which event this is. Absent on reports written before it,
   *  which read as `asked` for an `asking` status and `ended` otherwise. */
  event?: StopEvent
  /** How it stopped. `asking` is a turn still running, waiting on a question.
   *  `started` is a turn beginning: news, but nothing to decide. */
  status: 'started' | 'completed' | 'failed' | 'cancelled' | 'asking'
  /** The question it is waiting on, when it asked one. */
  interactionId: string
  /** First line of what it said, or its question. */
  summary: string
  /** How many of that session's latest turns in a row the World agent sent
   *  (RFC 0348 3d). Two means the next move is asking the person. */
  answeredInARow: number
  /** The start of what it said, a few lines, for the card (RFC 0348 3e).
   *  `summary` is one line for the model; this is what a person previews. */
  preview: string
  /** The person had the session open when it stopped (RFC 0348 3b). Written
   *  so the World agent knows, but no turn is queued: there is nothing to
   *  decide about what the person is reading. Absent means not seen. */
  seen?: boolean
  at: TimestampMs
}

/**
 * RFC 0350 3d: a reminder came due, written on each subscriber's log.
 *
 * An event, never the person typing: the agent that wakes on it decides
 * whether to speak, ask, or start the work. `key` (`scheduleId@dueAt`) is what
 * makes delivery idempotent: a log that already holds an event with this key
 * is not written again, so a clock retrying a delivery appends nothing.
 */
export const SCHEDULE_DUE_EXTENSION = 'mod.schedule_due' as const

/** Payload of a `mod.schedule_due` event (ExtensionData.payload). */
export interface ScheduleDueData {
  scheduleId: string
  /** The occurrence key, `scheduleId@dueAt`. */
  key: string
  /** The instant the person asked for, ISO UTC. */
  dueAt: string
  /** Delivered after its time because no clock host was awake. */
  late: boolean
  /** The reminder's text, as it was asked for. */
  text: string
  /** Whether this log's agent is the one that wakes. Quiet subscribers read
   *  it on their next turn. */
  wake: boolean
  at: TimestampMs
}

/**
 * RFC 0348 3e: what the World agent decided about one stop, written on its own
 * log by `decide_stop`. The card under each report reads it, so the person
 * sees whether the World agent handled it or left it for them.
 */
export const STOP_DECIDED_EXTENSION = 'mod.stop_decided' as const

/** What the World agent did with a stop. */
export type StopVerdict = 'answered' | 'yours' | 'nothing'

/** Payload of a `mod.stop_decided` event (ExtensionData.payload). */
export interface StopDecidedData {
  /** The session that stopped. */
  sessionId: string
  verdict: StopVerdict
  /** One line: what it answered, or why it is the person's. */
  note: string
  at: TimestampMs
}

/**
 * RFC 0289 — somebody answered a comment this session wrote.
 *
 * A comment thread is a conversation an agent can be part of, and until this
 * existed it was one the agent could never hear. Its comment sat on a document,
 * a person replied underneath, and nothing anywhere connected that reply to the
 * session that would have answered it. The reply reached the agent only if
 * someone separately typed "look at the comments".
 *
 * AN EVENT, not a user message, for the same reason `mod.session_replied` is:
 * appended as a message it would be indistinguishable from the person typing in
 * the composer, and an agent would treat a remark made on a document as an
 * instruction given to it.
 *
 * A POINTER, not a copy. The comment stays the source of truth, on the document
 * where it is anchored and where resolving or deleting it means something. This
 * carries enough to answer (the thread id, what it is anchored to, the first
 * line) and the agent replies with the `comment` tool, so the exchange stays in
 * one place instead of being half in a transcript that cannot be retracted.
 */
export const COMMENT_REPLIED_EXTENSION = 'mod.comment_replied' as const

/** Payload of a `mod.comment_replied` event (ExtensionData.payload). */
export interface CommentRepliedData {
  /** The reply itself. */
  commentId: string
  /** The thread to answer in: pass this as `parentId` to reply. */
  parentId: string
  /** The document the thread is anchored to. Empty for a session comment. */
  documentId: string
  /** Who replied (a userDocId, or an agent id). */
  author: string
  /** The passage the thread is about, when the anchor carries one. */
  anchorText: string
  /**
   * First line of the reply. The rest is read with the `comment` tool, for the
   * same reason `SessionRepliedData.summary` is bounded: inlining every reply
   * would grow the session log by the length of a conversation happening
   * somewhere else.
   */
  summary: string
  at: TimestampMs
}

/**
 * RFC 0292 — somebody NAMED this session in a body of prose.
 *
 * The third thing on this log that is a new thing to answer, beside
 * `mod.session_replied` and `mod.comment_replied`, and it is delivered the same
 * way for the same reasons: an event rather than a user message, so an agent
 * reading it knows a remark on a document was addressed to it rather than typed
 * to it in the composer; and a pointer rather than a copy, so the prose stays
 * where it was written, where resolving or deleting it still means something.
 *
 * A mention of a DOCUMENT never produces one of these. Naming a document is a
 * link; naming a session is the one thing in RFC 0292 that causes work to
 * happen.
 */
export const MENTIONED_EXTENSION = 'mod.mentioned' as const

/** Payload of a `mod.mentioned` event (ExtensionData.payload). */
export interface MentionedData {
  /** The comment the mention is in — the thread to answer in. */
  commentId: string
  /** The document that comment is anchored to. Empty for a session comment. */
  documentId: string
  /** Who wrote it (a userDocId, or an agent id). */
  author: string
  /** The passage the comment is about, when the anchor carries one. */
  anchorText: string
  /** First line of the prose that named this session, bounded like the others. */
  summary: string
  at: TimestampMs
}

// ============================================================================
// touched (RFC 0297)
// ============================================================================

/**
 * One act of this session touching one document (RFC 0297 §4a).
 *
 * APPENDED BY THE ACT, never inferred from it. The obvious alternative is to
 * read the `toolUses` already on the log, and it works in exactly one realm:
 * the three that call the document tools name them differently (`read_document`
 * in the cli, `mcp__mod__read_document` as injected, the sandboxed bridge's own
 * id) and `ToolUseBlock.arguments` is `unknown`. A reader that parsed names and
 * arguments would hold documents for one realm and silently nothing for the
 * other two.
 */
export const TOUCHED_EXTENSION = 'mod.touched' as const

/**
 * How a document came into a session's hands (RFC 0297 §4b).
 *
 * `mention` and `open` are DELIBERATE and pin; `read` and `write` are incidental
 * and decay. A refactor that writes forty documents must not pin forty, which is
 * the case that decides it.
 */
export type TouchKind = 'read' | 'write' | 'mention' | 'open'

/** Payload of a `mod.touched` event (ExtensionData.payload). */
export interface TouchedData {
  documentId: string
  kind: TouchKind
  /**
   * The document's `updatedAt` at the moment of the touch.
   *
   * Staleness is measured against what this session ACTUALLY SAW, not against
   * when it touched: two sessions reading the same document a minute apart have
   * seen the same bytes, and a clock comparison would call one of them stale.
   */
  sawUpdatedAt: TimestampMs
  at: TimestampMs
}

/**
 * A document put down (RFC 0297 §4f).
 *
 * Not a deletion. It leaves the bar until the next touch on that document brings
 * it back, which is why it folds like a touch rather than removing one: nothing
 * that was true stops being true, and the agent's copy of the list cannot end
 * up holding something the person's copy has dropped.
 */
export const PUT_DOWN_EXTENSION = 'mod.put_down' as const

/** Payload of a `mod.put_down` event (ExtensionData.payload). */
export interface PutDownData {
  documentId: string
  at: TimestampMs
}

// ============================================================================
// pairing (RFC 0301)
// ============================================================================

/** What passed the gate and started a pairing's turn (RFC 0301 §3b). */
export type FiringSignal = 'edit' | 'pause' | 'comment'

/**
 * A pairing acted (RFC 0301 §3f).
 *
 * BOOKKEEPING BESIDE THE SIGNAL, never the prompt. The signal is its own event
 * (a claimed comment, later an edit) and the queued run points at that event, so
 * the turn reads what happened in the words of what happened. This entry says
 * only that the signal came through a pairing, which is what the firing fold
 * and the gate need and what nothing else on the log records.
 */
export const FIRED_EXTENSION = 'mod.fired' as const

/** Payload of a `mod.fired` event (ExtensionData.payload). */
export interface FiredData {
  pairingId: string
  documentId: string
  signal: FiringSignal
  /** The signal's own entry, which the run's `promptMessageId` points at. */
  entryId: EntryId
  at: TimestampMs
}

/**
 * A turn chose to do nothing (RFC 0301 §3b).
 *
 * Recorded, so a quiet turn is distinguishable from a turn that never ran. The
 * run it names is the turn that called `pass`.
 */
export const PASSED_EXTENSION = 'mod.passed' as const

/**
 * This session claimed a comment that named nobody (RFC 0301 §3d).
 *
 * The same shape as `mod.mentioned`, and a different sentence: nobody asked
 * this session, it is answering because it is paired to the document.
 */
export const COMMENT_CLAIMED_EXTENSION = 'mod.comment_claimed' as const

/**
 * A person edited a document this session is paired to, and paused (RFC 0301
 * §3b, S6).
 *
 * Delivered by the app that made the edit, once typing has stopped, so one
 * event stands for a finished thought rather than a keystroke. The diff is
 * bounded: it says what moved, and the document is a read away.
 */
export const EDITED_EXTENSION = 'mod.edited' as const

/** Payload of a `mod.edited` event (ExtensionData.payload). */
export interface EditedData {
  documentId: string
  /** What changed, as removed and added lines, bounded. */
  diff: string
  /** The passage the person had selected when they paused, if any. */
  selectionLabel: string
  at: TimestampMs
}

/**
 * Which Mod branch this session works on, per project (RFC 0313 3j). The
 * latest per project wins, so a recreated VM replays the log and lands on the
 * branch it was on. An empty branch is main.
 */
export const BRANCH_EXTENSION = 'mod.branch' as const

/**
 * Somebody undid or restored over a change this session made (RFC 0313 3f).
 *
 * Told to the session whose change it was, and nobody else. Without it a paired
 * session notices the document moved and makes the edit it just lost all over
 * again; RFC 0301's `isUndoChange` guard covers only the one document a pairing
 * watches, and only at the moment of the undo.
 */
export const UNDONE_EXTENSION = 'mod.undone' as const

/** Payload of a `mod.undone` event (ExtensionData.payload). */
export interface UndoneData {
  documentId: string
  /** The change that was reversed, on an undo. */
  reverts: string
  /** The heads a restore came from, comma-joined, on a restore. */
  restores: string
  /** Who undid it, by `userDocId`, and their name when the window knew it. */
  by: string
  byName: string
  /** What was undone, in a line. */
  summary: string
  at: TimestampMs
}

// ============================================================================
// web pages (RFC 0303)
// ============================================================================

/**
 * The person pressed Take over on a web page this session was driving (RFC
 * 0303 3e).
 *
 * A record, not a prompt: nothing queues a turn on it. An agent mid-errand
 * already learns it from the next action being refused; this is what lets it,
 * or anyone reading the log later, see why.
 */
export const PAGE_TAKEN_OVER_EXTENSION = 'mod.page_taken_over' as const

/**
 * The person pressed Hand back: this session may act on the page again (RFC
 * 0303 3e). Queues a turn, so the agent carries on without being asked twice.
 */
export const PAGE_HANDED_BACK_EXTENSION = 'mod.page_handed_back' as const

/** Payload of a `mod.page_taken_over` or `mod.page_handed_back` event. */
export interface PageHandoffData {
  /** The page's url when the button was pressed. */
  url: string
  at: TimestampMs
}

/** Payload of a `mod.passed` event (ExtensionData.payload). */
export interface PassedData {
  runId: EntryId
  /** One line on why, for whoever reads the firing. Empty when none given. */
  note: string
  at: TimestampMs
}

// ============================================================================
// Envelope + entry + document
// ============================================================================

/** Adjacently-tagged payload envelope: `{ type, payload }` (automorph shape). */
export type EntryPayload =
  | { type: 'message'; payload: MessageData }
  | { type: 'run'; payload: RunData }
  | { type: 'inference_step'; payload: InferenceStepData }
  | { type: 'worker_migration'; payload: WorkerMigrationData }
  | { type: 'extension'; payload: ExtensionData }

export type EntryType = EntryPayload['type']

export interface Entry {
  id: EntryId
  parents: EntryId[]
  createdAt: TimestampMs
  payload: EntryPayload
}

/** An entry's envelope without the immutable core; the core is set by `append`. */
export type NewEntry = EntryPayload

export interface ToolWorker {
  name: string
  status: 'available' | 'busy' | 'unreachable'
  currentToolUseId?: ToolUseId | null
  /**
   * RFC 0240: last heartbeat (ms epoch). The liveness lease: a device whose
   * `lastSeen` is older than the TTL is reaped to `unreachable` so a run
   * addressed to it can fall back to another device.
   */
  lastSeen?: number
  /**
   * RFC 0240: this device's default inference model. Inference follows the
   * device: a run addressed to this device with no explicit model uses this.
   */
  defaultModel?: string
}

export interface LogDoc {
  schemaVersion: 1
  entries: Record<EntryId, Entry>
  toolWorkers: Record<ToolWorkerId, ToolWorker>
}
